import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { fetchMyFlightAccess } from '@/lib/api/flightAccess';
import type { FlightAccess } from '@/types/flightAccess';
import { useAuth } from './auth';

/**
 * The signed-in user's flight access, read once per session and kept.
 *
 * **For saying, never for filtering.** The server takes the access from the token on
 * every request and answers inside it, so a restricted user's board is already
 * narrowed by the time it arrives. This exists so the screens can TELL the reader
 * which flights they see — "Your flights: to or from Astana" — and so an empty board
 * reads as "nothing inside your access" rather than as a fault.
 *
 * Read once after sign-in, as the API guide suggests, and again when a screen asks
 * for it to be current (the profile does). An administrator's change applies to the
 * user's very next request on the server; this copy catches up on the next page load
 * or the next `reload`, and it is only ever a description of what the server does.
 */

interface FlightAccessContextValue {
  /** `null` until it is known: signed out, still loading, or the request failed. */
  access: FlightAccess | null;
  isLoading: boolean;
  /** The request failed. The board still works — only the description is missing. */
  hasFailed: boolean;
  /** Asks the server again. */
  reload: () => void;
}

const FlightAccessContext = createContext<FlightAccessContextValue | null>(null);

/** What one completed load produced, tagged with the load it answered. */
interface Outcome {
  key: string;
  access: FlightAccess | null;
  failed: boolean;
}

export const FlightAccessProvider = ({ children }: { children: ReactNode }) => {
  const { token, user } = useAuth();
  const [nonce, setNonce] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const selfId = user?.id ?? null;

  /*
    Keyed by the token, so signing in as somebody else is a new load rather than the
    previous person's access carried over. The id is in the key because the token
    alone does not say whose it is in this component, and the request needs it.
  */
  const key = token !== null && selfId !== null ? `${token}:${selfId}:${nonce}` : null;

  useEffect(() => {
    if (key === null || selfId === null) return undefined;

    const controller = new AbortController();

    fetchMyFlightAccess(selfId, controller.signal)
      .then((access) => setOutcome({ key, access, failed: false }))
      .catch(() => {
        // An abort is a newer load taking over, not a failure. A 401 never gets here
        // as anything useful either: the client has already ended the session.
        if (controller.signal.aborted) return;
        setOutcome({ key, access: null, failed: true });
      });

    return () => controller.abort();
  }, [key, selfId]);

  const reload = useCallback(() => setNonce((current) => current + 1), []);

  const isCurrent = key !== null && outcome?.key === key;

  /*
    The previous answer stays in hand while a reload runs, so a note on screen does not
    blink out and back. It is dropped the moment the SESSION changes, though: another
    user's access must never be shown, even for the length of one request.
  */
  const sameSession = key !== null && outcome !== null && sessionOf(outcome.key) === sessionOf(key);

  const value = useMemo<FlightAccessContextValue>(
    () => ({
      access: sameSession ? outcome.access : null,
      isLoading: key !== null && !isCurrent,
      hasFailed: isCurrent && outcome.failed,
      reload,
    }),
    [sameSession, outcome, key, isCurrent, reload],
  );

  return <FlightAccessContext.Provider value={value}>{children}</FlightAccessContext.Provider>;
};

/** The key without its nonce — the session the answer belongs to. */
const sessionOf = (key: string): string => key.slice(0, key.lastIndexOf(':'));

export const useFlightAccess = (): FlightAccessContextValue => {
  const context = useContext(FlightAccessContext);

  if (!context) {
    throw new Error('useFlightAccess must be used inside <FlightAccessProvider>');
  }

  return context;
};

/**
 * The stations a reader may pick on a board: their access's airports, or `null` when
 * every station is theirs — no restriction, a restriction by airline only, or an
 * access not known yet.
 *
 * Narrowing a station dropdown is a choice of what to OFFER, not a filter: the server
 * already answers inside the access whatever is picked. Offering only the reader's own
 * airports is the product's call — a station outside them would only ever show the far
 * end of flights they already see.
 *
 * The array is the access's own, so its identity holds until the access is read again
 * and a memo can depend on it.
 */
export const useAccessStations = (): readonly string[] | null => {
  const { access } = useFlightAccess();
  return access !== null && access.restricted && access.airports.length > 0 ? access.airports : null;
};
